import { isBefore } from 'date-fns'
import { IOrderItem, TOrderPlatform, TOrderType } from '../../model/order.store'
import { ICustomer } from '../../model/customer.model'
import { some, get, reduce } from 'lodash'
import { getSubTOtalWithoutPricedGift, getItemDiscount } from './discount-utils'
import { getItemSoldPrice } from '../order.util'

/**
 * Chương trình tặng Bộ dao dĩa cao cấp cho khách hàng thân thiết
 * Áp dụng từ 03/09/2025
 * Điều kiện:
 * - Chỉ áp dụng cho SAVOR_SHOP
 * - Customer phải có scakePremiumCutleryGiftResult.isEligible = true (từ backend)
 * - Tổng đơn >= 200k
 * - Phải có bánh sinh nhật trong đơn hàng
 */
export function checkCutleryGiftEligibility({
  customer,
  orderPlatform,
  items,
  orderType,
  saleTotal
}: {
  customer: ICustomer | null
  orderPlatform: TOrderPlatform
  items: IOrderItem[]
  orderType: TOrderType
  saleTotal?: number
}): {
  isEligible: boolean
  giftSku: string
  giftText: string
  maxQuantity: number
} {
  const CUTLERY_GIFT_SKU = 'V10089'
  const defaultResult = {
    giftSku: CUTLERY_GIFT_SKU,
    giftText: 'Bộ dao dĩa cao cấp',
    maxQuantity: 1
  }
  const rejectedResult = {
    ...defaultResult,
    isEligible: false
  }

  // Áp dụng từ 03/09/2025
  if (isBefore(new Date(), new Date('2025-09-03T00:00:00+07:00'))) {
    return rejectedResult
  }

  // Chỉ áp dụng cho SAVOR_SHOP
  if (orderPlatform !== 'SAVOR_SHOP') {
    return rejectedResult
  }

  if (!items || !get(items, 'length', 0)) {
    return rejectedResult
  }

  // Kiểm tra customer có thông tin gift eligibility từ backend
  if (!get(customer, 'scakePremiumCutleryGiftResult.isEligible', false)) {
    return rejectedResult
  }

  // Tổng đơn hiện tại không đủ 200k
  const orderTotal = saleTotal || getSubTOtalWithoutPricedGift(items, orderPlatform, orderType)
  if (orderTotal < 200000) {
    return rejectedResult
  }

  // Kiểm tra có bánh sinh nhật trong đơn hàng
  const hasBirthdayCake = some(items, item => item.productCatWorkName === 'Bánh sinh nhật')
  if (!hasBirthdayCake) {
    return rejectedResult // Không đủ điều kiện, không báo lỗi
  }

  return {
    ...defaultResult,
    isEligible: true
  }
}

// ============================================
// STAFF DISCOUNT - Giảm giá nhân viên
// ============================================
export function getStaffDiscountByBusiness({
  breadDiscount,
  cakeDiscount,
  percent,
  items,
  discountItemMap,
  orderPlatform,
  orderType
}: {
  breadDiscount: boolean
  cakeDiscount: boolean
  percent: number
  items: IOrderItem[]
  discountItemMap: any
  orderPlatform: TOrderPlatform
  orderType: TOrderType
}) {
  return reduce(
    items,
    (result, item) => {
      if (item.sku !== 'V1408' && getItemDiscount(item, discountItemMap) > 0) {
        // xét giống withoutDiscountsSubTotal
        return result
      }
      const soldPrice = getItemSoldPrice({
        item,
        orderPlatform,
        orderType
      })
      // xét theo birthday-cake vì đã xử lý sync db và convert theo productcat Bánh sinh nhật và Phụ kiện BSN ở hàm convertCategory
      if (!breadDiscount && item.category !== 'birthday-cake') {
        // product savor bread nhưng ko được giảm giá
        return result
      }
      if (!cakeDiscount && item.category === 'birthday-cake') {
        // product savor cake nhưng ko được giảm giá
        return result
      }
      return result + (soldPrice * item.quantity * percent) / 100
    },
    0
  )
}

// ============================================
// DRINK DISCOUNT - Giảm giá đồ uống kèm bánh mì/xôi
// ============================================
interface DrinkDiscountMap {
  [key: string]: {
    salePrice: number
    description: string
  }
}

export const drinkDiscountMap: DrinkDiscountMap = {
  V2331: {
    salePrice: 19000,
    description: 'Trà Ô Long chanh leo (M)'
  },
  V2333: {
    salePrice: 15000,
    description: 'Trà Ô Long kim quất (M)'
  },
  V2329: {
    salePrice: 22000,
    description: 'Trà Ô Long nhài sữa trân châu (M)'
  },
  V0608: {
    salePrice: 22000,
    description: 'Trà sữa trân châu đường đen (M)'
  },
  V0400: {
    salePrice: 15000,
    description: 'Cà phê đen đá 250ml'
  },
  V0401: {
    salePrice: 19000,
    description: 'Cà phê sữa đá 250ml'
  },
  V1091: {
    salePrice: 19000,
    description: 'Bạc xỉu đá'
  },
  V0001: {
    salePrice: 15000,
    description: 'Sữa chua uống Savor chai 250ml'
  }
}

/**
 * @deprecated
 */
export function getDrinkDiscountQuantity(items: IOrderItem[]) {
  return items.reduce((result, item) => {
    if (!['bread', 'sticky-rice'].includes(item.category)) {
      return result
    }
    if (item.isCombo || item.comboId) {
      result -= item.quantity
    }
    result += item.quantity
    return result
  }, 0)
}
